import { Dispatch, SetStateAction, useState } from "react";
import TextInput from "../shared/inputs/TextInput";
import Dropdown, { SingleStringSelectChangeValue } from "../shared/inputs/Dropdown";
import { CreateSetForm } from "./CreateListModal";
import { InfoIcon } from "../../assets/icons";
import { CollectionPeriods } from "../../types/SharedTypes";
import { collectionPeriods, getMaxKeyForPeriod } from "../../assets/general";
import { useGetCollectionById } from "../../api/data-access";
import SwitchOptionButton from "../shared/inputs/SwitchOptionButton";

type Props = {
  form: CreateSetForm;
  setForm: Dispatch<SetStateAction<CreateSetForm>>;
  isEdit?: boolean;
  isSet?: boolean;
  isCurrent?: boolean;
  collectionId?: string;
};

const CreateListSetTab = ({form, setForm, isEdit, collectionId, isSet, isCurrent}: Props) => {

  const InfoBar = () => {
    return (
      <div className="item-row tile padding-small center-vertical" data-small={true}>
        <div className="icon-display secondary small">
          <InfoIcon />
        </div>
        <p className="subtitle-large spaced">
          {!isSet
            ? <>A series is a periodic grouping of lists or collections. <span className="italic secondary">"The best music of the month."</span></> 
            : isCurrent 
            ? 'Changes to these settings will effect only the current period collection.'
            : 'Changes to these settings will effect all collections in this smart collection set.'
          }
        </p>
      </div>
    )
  }

  return (
    <section className="padding item-list large bottom-border secondary-border">
      <InfoBar />
      <ListSetForm form={form} isEdit={isEdit} setForm={setForm} collectionId={collectionId} />
    </section>
  )
};

type ListSetFormProps = {
  form: CreateSetForm;
  setForm: Dispatch<SetStateAction<CreateSetForm>>;
  isEdit?: boolean;
  collectionId?: string;
}

const ListSetForm = ({form, isEdit, setForm, collectionId}: ListSetFormProps) => {
  
  const collection = useGetCollectionById(collectionId || '', !!collectionId);
  
  const [localStartingKey, setLocalStartingKey] = useState((form.startingKey || 1).toString());
  
  const currentPeriod = collectionPeriods.find(opt => opt.value === form.period) || null;
  const isListSet = !!collection.data?.listSet;
  
  const updatePeriod = (data: SingleStringSelectChangeValue) => {
    if (!data) return;

    setForm((prev) => ({
      ...prev,
      period: data.value as CollectionPeriods,
    }));
  };
  
  const handleUpdateStartingKey = (val: string) => {
    const value = Number(val);
    if (val === '') {
      setLocalStartingKey(val);
      return;
    }
    if (Object.is(value, NaN) || value === 0) return;
    
    const maxValue = getMaxKeyForPeriod(form.period);
    
    if (value > maxValue) return;
    
    setLocalStartingKey(value.toString());
    setForm((prev) => ({
      ...prev,
      startingKey: value
    }));
  }
  
  return (
    <>
      {/* <div className="tile padding-small item-list" data-small={true}>
        <p className="subtitle-small secondary">Smart Collections</p>
        <p className="body spaced"><span className="weight-bold">Smart collections are a set of periodically created collections.</span> For example, if the period is "weekly", a new collection will be automatically created in the set every week. This set will appear as one single collection in your lists, with the addition of a timeline to navigate the created periods.</p>
      </div> */}
      <div className="item-list med">
        <label aria-required={true} className="subtitle-medium indent">{"Period"}</label>
        <Dropdown
          value={currentPeriod}
          options={collectionPeriods}
          onChange={updatePeriod}
          error={false}
          placeholder={'Select period...'}
          isSearchable={false}
        />
      </div>
      {form.period !== "seasonal" && 
        <TextInput
          value={localStartingKey}
          onChange={handleUpdateStartingKey}
          label={`starting date (1 - ${getMaxKeyForPeriod(form.period)})`}
          id='startingkeyinput'
          required
          placeholder={"1"}
          note={form.period === 'weekly'
            ? `Example: '2' would create a new collection every Tuesday`
            : form.period === 'monthly'
            ? `Example: '14' would create a new collection on the 14th of every month.`
            : form.period === 'yearly'
            ? `Example: '284' would create a new collection on October 11th of each year.`
            : undefined
          }
        />
      }
      {isEdit && isListSet && 
        <div className="item-list">
          <div className="item-row">
            <input
              checked={form.isSetDisabled}
              type={"checkbox"}
              id={'setDisabled'}
              onChange={(e) => setForm(prev => ({...prev, isSetDisabled: e.target.checked}))}
            />
            <label htmlFor={'setDisabled'} className={'body'}>Disable Smart Collection</label>
          </div>
          <p className="subtitle-small weight-light secondary">Stops periods from being automatically created.</p>
        </div>
      }
      <SwitchOptionButton
        label={"Series Type"}
        required
        options={[{
          text: 'List',
          selected: form.type === 'list',
          onClick: () => setForm(prev => ({...prev, type: 'list'}))
        }, {
          text: 'Collection',
          selected: form.type === 'collection',
          onClick: () => setForm(prev => ({...prev, type: 'collection'}))
        }]}
      />
    </>
  );
}

export default CreateListSetTab;